import React from 'react';
import { inr, day } from '../utils/format';
import Barcode from './Barcode';

// ── Shared barcode-label design ─────────────────────────────────
// The label template (size, layout, which fields show, border, footer text)
// is designed once in Settings and saved as the `labelTemplate` setting; the
// print dialog then renders every label from that finalized template.

// Physical label stock sizes (width × height in mm). "custom" lets the user
// dial in any dimensions to match their sticker roll / sheet.
export const LABEL_PRESETS = [
  { key: '38x25', label: '38 × 25 mm', w: 38, h: 25 },
  { key: '50x25', label: '50 × 25 mm', w: 50, h: 25 },
  { key: '50x30', label: '50 × 30 mm', w: 50, h: 30 },
  { key: '65x38', label: '65 × 38 mm', w: 65, h: 38 },
  { key: '75x50', label: '75 × 50 mm', w: 75, h: 50 },
  { key: 'custom', label: 'Custom…', w: 50, h: 30 },
];

export const FIELD_OPTS = [
  ['name', 'Product name'], ['pack', 'Pack / variant'], ['price', 'Price'],
  ['oneLine', 'Name, pack & price on one line'], ['mrp', 'MRP'],
  ['barcodeNo', 'Barcode number'], ['mfg', 'Mfg date'], ['shelfLife', 'Shelf life'],
  ['bestBefore', 'Best before'],
];

export const DEFAULT_TEMPLATE = {
  preset: '50x30',
  customW: 50,
  customH: 30,
  // 'continuous' by default: it is the only one that does not depend on the
  // printer driver having been told the sticker size.
  layout: 'continuous',   // 'roll' = a page per label; 'continuous' = one long page; 'sheet' = A4 grid
  rotate: 0,        // 0 or 90 — turn the printed content when the roll feeds the label sideways
  printScale: 100,  // % — trim the whole label to match what the printer actually feeds
  symbology: 'CODE128',   // CODE128 packs digits two to a symbol; CODE39 is ~40% wider
  companyLine: '',  // optional footer text (brand / FSSAI lic. no.)
  fields: { name: true, pack: true, price: true, oneLine: false, mrp: false, barcodeNo: true, mfg: true, shelfLife: true, bestBefore: true },
};

// Merge a stored template with the defaults so older/partial saves still render.
// Defensive: a stored value may arrive as a JSON string (settings API) — parse it,
// and only pick known keys so junk (e.g. from a previously char-spread save) is
// dropped rather than carried forward.
export const normTemplate = (t) => {
  let o = t;
  if (typeof o === 'string') { try { o = JSON.parse(o); } catch (e) { o = null; } }
  if (!o || typeof o !== 'object' || Array.isArray(o)) o = {};
  return {
    preset: o.preset || DEFAULT_TEMPLATE.preset,
    customW: o.customW != null ? o.customW : DEFAULT_TEMPLATE.customW,
    customH: o.customH != null ? o.customH : DEFAULT_TEMPLATE.customH,
    layout: o.layout || DEFAULT_TEMPLATE.layout,
    rotate: Number(o.rotate) === 90 ? 90 : 0,
    printScale: Math.min(120, Math.max(50, Number(o.printScale) || 100)),
    symbology: o.symbology === 'CODE39' ? 'CODE39' : 'CODE128',
    companyLine: typeof o.companyLine === 'string' ? o.companyLine : DEFAULT_TEMPLATE.companyLine,
    // 'packPrice' was one switch for both; a design saved before the split keeps
    // whatever it had.
    fields: (() => {
      const f = { ...DEFAULT_TEMPLATE.fields, ...(o.fields && typeof o.fields === 'object' ? o.fields : {}) };
      if (o.fields && o.fields.packPrice != null && o.fields.pack == null && o.fields.price == null) {
        f.pack = !!o.fields.packPrice;
        f.price = !!o.fields.packPrice;
      }
      return f;
    })(),
  };
};

// Resolve a template to its physical width/height in mm.
export const templateSize = (t) => {
  if (t.preset === 'custom') return { W: Math.max(20, Number(t.customW) || 50), H: Math.max(15, Number(t.customH) || 30) };
  const p = LABEL_PRESETS.find((x) => x.key === t.preset) || LABEL_PRESETS[2];
  return { W: p.w, H: p.h };
};

// Derive barcode + font sizing from the label's physical size (mm → px @96dpi)
// so everything scales proportionally to whatever size is chosen.
export const labelDims = (w, h) => {
  const px = h * 3.7795;
  return {
    // Taller bars again, now the padding is not taking 4mm of the label: about
    // a third of the height, which on a 50×32 gives ~9mm — a comfortable read
    // for a handheld at speed rather than the bare minimum.
    barH: Math.max(14, Math.round(px * (w >= 60 ? 0.34 : 0.30))),
    barW: Math.min(2, Math.max(0.9, +(w * 0.02).toFixed(2))),
    barF: Math.max(6, Math.round(px * 0.07)),
    // Smaller type across the label: the name, the pack-and-price line and the
    // dates. Bigger is not better on a 50mm sticker — it is what forces the fit
    // to shrink everything anyway, and what pushes a long name down to nothing.
    nameF: Math.max(7, Math.round(px * (h <= 25 ? 0.105 : 0.115))),
    metaF: Math.max(5, Math.round(px * 0.065)),
  };
};

// What the label needs, in px, for the rows that are switched on. Estimating is
// enough: the point is to know when the design is asking for more room than the
// sticker has, which at 50×32 with every field on it is — the "Best before" line
// simply disappeared under overflow:hidden, silently, on every print.
const MM_PX = 3.7795;
// 0.5mm inside the label: enough to keep ink off a die-cut edge a roll can drift
// past, and little enough that the design gets the sticker.
export const LABEL_PAD_MM = 0.5;

// Thermal heads print on a dot grid: 203dpi = 8 dots/mm = 0.125mm per dot.
export const DOT_MM = 0.125;
export const MM_PER_PX = 25.4 / 96;

// The narrow bar, snapped to whole dots and to what a scanner can read.
// GS1 puts the floor for Code 128 at 0.25mm; below that a handheld starts
// guessing. Wider is better, so take the widest whole number of dots whose
// barcode still fits the label.
export function barWidthPx(modules, availPx) {
  const availMm = availPx * MM_PER_PX;
  const rawMm = availMm / Math.max(1, modules);
  const dots = Math.max(2, Math.floor(rawMm / DOT_MM));   // 2 dots = 0.25mm floor
  return (dots * DOT_MM) / MM_PER_PX;
}

// Roughly how many modules a Code 128 of this value takes: mode C packs two
// digits per symbol, plus start, check and stop.
export const code128Modules = (value) => {
  const n = String(value || '').length;
  return (Math.ceil(n / 2) + 3) * 11 + 2;
};
// The product name is set on one line, always: a wrapped name eats the height the
// barcode and the dates need, and reads worse on a 50mm sticker than a smaller
// font does. This is the largest size at which it still fits the width.
//
// MEASURED, not estimated. A per-character guess is fine for mixed case and
// badly wrong for the shop's names, which are capitals — "AJMEER KALAKAND
// SPECIAL" in Arial Bold is a third wider than the same count of lower-case
// letters, and the difference is a name clipped mid-word on the sticker.
let measureCtx;
function textWidthPx(text, px, weight = 700) {
  if (typeof document === 'undefined') return String(text || '').length * px * 0.62;   // no DOM (tests)
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  if (!measureCtx) return String(text || '').length * px * 0.62;
  measureCtx.font = `${weight} ${px}px Arial, sans-serif`;
  return measureCtx.measureText(String(text || '')).width;
}

export function nameFontPx(name, availW, max) {
  const text = String(name || '');
  if (!text) return Math.max(6, Math.min(max, 8));
  for (let px = Math.floor(max); px >= 5; px--) {
    if (textWidthPx(text, px) <= availW) return px;
  }
  return 5;   // a 30-character name on a 50mm label lands around 9px, well above this
}

export function contentHeightPx({ D, fields, name, shelfLife, companyLine, availW }) {
  const f = fields;
  let px = 0;
  if (f.name) px += nameFontPx(name, availW, D.nameF) * 1.05;   // one line, by construction
  // Combined, the pack and price ride on the name's row and cost no height.
  if ((f.pack || f.price) && !f.oneLine) px += (D.metaF + 1) * 1.25;
  px += D.barH + (f.barcodeNo ? D.barF + 2 : 0) + 4;
  const dateLines = (f.mfg ? 1 : 0) + (f.shelfLife && shelfLife ? 1 : 0) + (f.bestBefore ? 1 : 0);
  if (dateLines) px += dateLines * D.metaF * 1.2 + 1;
  if (companyLine) px += D.metaF * 1.15 + 1;
  return px;
}

// Shrink the type and the barcode together until the design fits the label. Never
// below 55%: past that it is unreadable, and the honest answer is a bigger
// sticker or fewer fields, not a smaller font.
export function fitDims({ t, product, variant, mfgDate, cW, cH }) {
  const D = labelDims(cW, cH);
  const availH = (cH - 2 * LABEL_PAD_MM) * MM_PX;
  const availW = (cW - 2 * LABEL_PAD_MM) * MM_PX;
  const needed = contentHeightPx({
    D, fields: t.fields, name: t.fields.oneLine ? [product.name, packLine(t.fields, variant)].filter(Boolean).join('  ') : product.name,
    shelfLife: product.shelfLife, companyLine: t.companyLine, availW,
  });
  const headline = t.fields.oneLine
    ? [product.name, packLine(t.fields, variant)].filter(Boolean).join('  ')
    : product.name;
  const withName = (dims) => ({ ...dims, nameF: nameFontPx(headline, availW, dims.nameF) });
  if (needed <= availH) return { ...withName(D), scale: 1 };

  // Shrinking the type re-wraps the name, which can leave it still over — so
  // measure again after each pass. Three is enough to settle; the floor stops it
  // chasing a fit that would be unreadable anyway.
  let cur = { ...D };
  let scale = 1;
  for (let pass = 0; pass < 5; pass++) {
    const need = contentHeightPx({
      D: cur, fields: t.fields, name: headline, shelfLife: product.shelfLife,
      companyLine: t.companyLine, availW,
    });
    if (need <= availH) break;
    const k = Math.max(0.55 / scale, (availH * 0.97) / need);
    scale = Math.max(0.55, scale * k);
    cur = {
      barH: Math.max(14, Math.round(D.barH * scale)),
      barW: D.barW,
      barF: Math.max(6, Math.round(D.barF * scale)),
      nameF: Math.max(7, Math.round(D.nameF * scale)),
      metaF: Math.max(5, Math.round(D.metaF * scale)),
    };
  }
  return { ...withName(cur), scale };
}

// Best-before = manufacturing date + the product's shelf life (e.g. "6 months").
export const addShelfLife = (mfg, shelfLife) => {
  const m = String(shelfLife || '').match(/(\d+)\s*(day|week|month|year)/i);
  if (!m || !mfg) return '';
  const n = Number(m[1]); const u = m[2].toLowerCase(); const d = new Date(mfg);
  if (u === 'day') d.setDate(d.getDate() + n);
  else if (u === 'week') d.setDate(d.getDate() + n * 7);
  else if (u === 'month') d.setMonth(d.getMonth() + n);
  else d.setFullYear(d.getFullYear() + n);
  return d.toISOString().slice(0, 10);
};

// The pack-and-price line, with only the parts that are switched on AND present.
// A loose product has no pack label, and a blank one must leave no separator
// behind — "· ₹680.00" is worse than nothing.
export const packLine = (fields, variant) => [
  fields.pack ? (variant.weightLabel || '') : '',
  fields.price && Number(variant.sellingPrice) > 0 ? inr(variant.sellingPrice) : '',
  fields.mrp && Number(variant.mrp) > 0 ? `MRP ${inr(variant.mrp)}` : '',
].filter(Boolean).join(' · ');

// One rendered label. `product`/`variant` carry the data; `template` the design.
export const LabelBox = ({ template, product, variant, mfgDate }) => {
  const t = normTemplate(template);
  const { W, H } = templateSize(t);
  // Rotated labels lay the content out on the label's OTHER axis and turn it a
  // quarter turn, so the printed result still fits inside the same sticker.
  const rot = t.rotate === 90;
  const cW = rot ? H : W;
  const cH = rot ? W : H;
  const D = fitDims({ t, product, variant, mfgDate, cW, cH });
  const f = t.fields;
  const shelfLife = product.shelfLife;
  const bestBefore = addShelfLife(mfgDate, shelfLife);
  const content = (
    <div className="print-label-inner" style={{ width: `${cW - 2 * LABEL_PAD_MM}mm`, height: `${cH - 2 * LABEL_PAD_MM}mm`, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', overflow: 'hidden', fontFamily: 'Arial, sans-serif', transform: rot ? 'rotate(90deg)' : undefined }}>
      {f.name && (
        <div style={{ fontWeight: 700, fontSize: D.nameF, lineHeight: 1.05, width: '100%', whiteSpace: 'nowrap', overflow: 'hidden' }}>
          {product.name}
          {f.oneLine && packLine(f, variant) ? <span style={{ fontWeight: 400 }}>{'  '}{packLine(f, variant)}</span> : null}
        </div>
      )}
      {!f.oneLine && packLine(f, variant) && <div style={{ fontSize: D.metaF + 1, color: '#333' }}>{packLine(f, variant)}</div>}
      <Barcode value={variant.barcode} format={t.symbology} height={D.barH}
        width={barWidthPx(t.symbology === 'CODE39' ? (String(variant.barcode || '').length + 2) * 16 : code128Modules(variant.barcode), cW / MM_PER_PX)}
        fontSize={D.barF} displayValue={f.barcodeNo} margin={2} />
      {(f.mfg || f.shelfLife || f.bestBefore) && (
        <div style={{ fontSize: D.metaF, lineHeight: 1.2, marginTop: 1 }}>
          {f.mfg && <div>Mfg: {mfgDate ? day(mfgDate) : '—'}</div>}
          {f.shelfLife && shelfLife && <div>Shelf life: {shelfLife}</div>}
          {f.bestBefore && bestBefore && <div>Best before: {day(bestBefore)}</div>}
        </div>
      )}
      {t.companyLine && <div style={{ fontSize: D.metaF, color: '#333', marginTop: 1, lineHeight: 1.15 }}>{t.companyLine}</div>}
    </div>
  );
  // The outer box is exactly one label — and, in roll mode, exactly one page.
  return (
    <div className="print-label"
      style={{ width: `${W}mm`, height: `${H}mm`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {content}
    </div>
  );
};

// Injects the print @page size for roll mode so a thermal printer feeds exactly
// one label per page. No-op for sheet layout (uses the global A4 @page).
export const LabelPageStyle = ({ template }) => {
  const t = normTemplate(template);
  if (t.layout !== 'roll') return null;
  const { W, H } = templateSize(t);
  // Force the paper to the exact label size with no margin. Declared both
  // top-level and inside @media print (and margin !important) so it reliably
  // overrides any other print @page rules on the page (e.g. the payslip block),
  // which otherwise fall back to A4/Letter and waste roll labels.
  const rule = `@page { size: ${W}mm ${H}mm; margin: 0 !important; }`;
  // Pin every label to exactly one page. `height: 100%` resolved against the
  // sheet's auto height, so a label whose contents ran tall pushed the rest of
  // itself onto the NEXT sticker — the printed run then drifted, leaving blank
  // labels between the good ones. mm values remove the ambiguity, and the
  // barcode is capped to the label width so it can never widen the page.
  const box = `@media print {
    .label-sheet.roll .print-label { width: ${W}mm !important; height: ${H}mm !important; overflow: hidden !important; }
    .label-sheet.roll .print-label svg { max-width: 100% !important; }
  }`;
  return <style>{`${rule} @media print { ${rule} } ${box}`}</style>;
};

const SAMPLE = {
  product: { name: 'Chicken Pickle', shelfLife: '6 months' },
  variant: { weightLabel: '250g', sellingPrice: 560, mrp: 650, barcode: '2890000000036' },
};

// The design surface used in Settings: controls on the left, live preview right.
export const LabelDesigner = ({ value, onChange, disabled }) => {
  const t = normTemplate(value);
  const { W, H } = templateSize(t);
  const set = (patch) => onChange({ ...t, ...patch });
  const setField = (k) => onChange({ ...t, fields: { ...t.fields, [k]: !t.fields[k] } });
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="label-designer">
      <div className="label-designer-controls">
        <div className="flex gap" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ maxWidth: 150 }}><label>Label size</label>
            <select disabled={disabled} value={t.preset} onChange={(e) => set({ preset: e.target.value })}>{LABEL_PRESETS.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}</select>
          </div>
          {t.preset === 'custom' && (
            <div className="field" style={{ maxWidth: 190 }}><label>Width × height (mm)</label>
              <div className="flex gap" style={{ gap: '.4rem', alignItems: 'center' }}>
                <input type="number" min="20" disabled={disabled} value={t.customW} onChange={(e) => set({ customW: e.target.value })} style={{ width: 74 }} />
                <span className="muted">×</span>
                <input type="number" min="15" disabled={disabled} value={t.customH} onChange={(e) => set({ customH: e.target.value })} style={{ width: 74 }} />
              </div>
            </div>
          )}
          <div className="field" style={{ maxWidth: 200 }}><label>Print layout</label>
            <select disabled={disabled} value={t.layout} onChange={(e) => set({ layout: e.target.value })}>
              <option value="continuous">Label roll — one long page (recommended)</option>
              <option value="roll">Label roll — one page per label</option>
              <option value="sheet">A4 sheet (many)</option>
            </select>
          </div>
          <div className="field" style={{ maxWidth: 170 }}><label>Barcode type</label>
            <select disabled={disabled} value={t.symbology} onChange={(e) => set({ symbology: e.target.value })}>
              <option value="CODE128">Code 128 (denser)</option>
              <option value="CODE39">Code 39</option>
            </select>
          </div>
          <div className="field" style={{ maxWidth: 130 }}><label>Print scale</label>
            <div className="flex gap" style={{ gap: '.35rem', alignItems: 'center' }}>
              <input type="number" min="50" max="120" step="1" disabled={disabled}
                value={t.printScale} onChange={(e) => set({ printScale: Number(e.target.value) })} style={{ width: 70 }} />
              <span className="muted">%</span>
            </div>
          </div>
          <div className="field" style={{ maxWidth: 200 }}><label>Print direction</label>
            <select disabled={disabled} value={t.rotate} onChange={(e) => set({ rotate: Number(e.target.value) })}>
              <option value={0}>Normal</option>
              <option value={90}>Rotated 90°</option>
            </select>
          </div>
        </div>
        <div className="flex gap" style={{ flexWrap: 'wrap', marginTop: '.7rem', gap: '.1rem .9rem' }}>
          <span className="muted small" style={{ alignSelf: 'center' }}>Show on label:</span>
          {FIELD_OPTS.map(([k, lbl]) => <label key={k} className="chk"><input type="checkbox" disabled={disabled} checked={!!t.fields[k]} onChange={() => setField(k)} /> {lbl}</label>)}
        </div>
        <div className="field" style={{ maxWidth: 340, marginTop: '.7rem' }}><label>Footer line (optional — brand / FSSAI lic. no.)</label>
          <input disabled={disabled} value={t.companyLine} placeholder="e.g. Avakaaya Foods · FSSAI 10123456789012" onChange={(e) => set({ companyLine: e.target.value })} />
        </div>
        <p className="muted small" style={{ marginTop: '.6rem' }}>
          <strong>Print scale</strong> shrinks the whole label — page, box and type together — against what the printer actually
          feeds. Print a test label, measure what came out, and set the scale to the ratio: a 50mm design landing as 55mm wants
          about 90%. The preview stays at the design size; the scale is the printer's correction, not a smaller design.
        </p>
        <p className="muted small" style={{ marginTop: '.6rem' }}>
          <strong>If a label spans two stickers, or leaves space at the top:</strong> the driver's page is taller than the sticker,
          and no design can shrink the driver's page. Use <em>one long page</em> — the run goes as a single page as long as the
          labels come to, with no page breaks for the driver to get wrong, and the printer's own gap sensor finds each sticker.
          Setting the stock size (below) fixes it properly; this works without touching the driver.
        </p>
        <p className="muted small" style={{ marginTop: '.6rem' }}>
          <strong>If a run eats stickers:</strong> the size here sets the page, but the printer's own stock size decides how far the
          roll feeds. In Windows, open <span className="mono">Printers &amp; scanners → your label printer → Printing preferences</span>
          and set the paper/stock to exactly the same size (and the media type to labels with the gap sensor on). A driver still set
          to A4 or a 100mm stock will feed several stickers for every label, whatever the design says.
        </p>
        <p className="muted small" style={{ marginTop: '.6rem' }}>
          <strong>Printing without the preview dialog:</strong> a web page cannot skip it, but Chrome and Edge can be started
          with <span className="mono">--kiosk-printing</span>, which prints straight to the default printer. On the till machine,
          copy the browser shortcut, open its Properties, and add <span className="mono"> --kiosk-printing</span> to the end of the
          Target box — then set the label roll or bill printer as the Windows default. Nexus prints each document on its own
          page size, so the default printer is all it needs.
        </p>
      </div>
      <div className="label-designer-preview">
        <span className="muted small">
          Preview · {W} × {H} mm{t.rotate === 90 ? ' · rotated' : ''}
          {t.printScale !== 100 ? ` · printed at ${t.printScale}%` : ''}
        </span>
        <div className="label-sheet" style={{ display: 'flex' }}>
          <LabelBox template={t} product={SAMPLE.product} variant={SAMPLE.variant} mfgDate={today} />
        </div>
      </div>
    </div>
  );
};
